import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn"; // 시스템 공통 유틸
import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { lpcoEnvs } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * Common Schema
 * !== 공통 스키마 ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 경로 Array
 */
export const BASE = {
    path: `${lpcoEnvs.base}/comn`,
    api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/comn/comn-srvc`,
    zmp_api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/intf/ntfc/zmp`,
    rprt_api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/comn/rprt`,
    comnAprvApi: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/ptl/aprv/aprv/aprv-mgmt`,
    api_v1: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/lpco/fee`,
    nodes: [
        { path: "/", label: "Collection" },
        { path: "/comn", label: "Common" },
    ],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS_COMN = {
    // Get TANSAD Info !== TANSAD 정보 조회 ==!
    getTansad: (id: any) => {
        return api.get(`${BASE.api}/${id}/tansad`);
    },
    // Get TANSAD Tax Info !== TANSAD 세액 정보 조회 ==!
    getTansadTaxDtl: (data: any) => {
        return api.get(`${BASE.api}/getTansadTaxDtl`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Bill Info !== 고지 정보 조회 ==!
    getBll: (id: any) => {
        return api.get(`${BASE.api}/${id}/bll`);
    },
    getBllDtl: (data: any) => {
        return api.get(`${BASE.api}/bll`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Print Bill !== 고지서 인쇄 ==!
    printBill: (id: any) => {
        return api.get(`${BASE.rprt_api}/bll/${id}`, {
            responseType: "arraybuffer",
        });
    },
    printRcpt: (id: any) => {
        return api.get(`${BASE.rprt_api}/rcpt/${id}`, {
            responseType: "arraybuffer",
        });
    },
    // Print Tax Bill !== 세금 고지서 인쇄 ==!
    printTaxBill: (id: any) => {
        return api.get(`${BASE.api}/${id}/printTaxBll`, {
            responseType: "arraybuffer",
        });
    },
    // Print Rebate Refund !== 리베이트 환급 인쇄 ==!
    printRebtRfun: (id: any) => {
        return api.get(`${BASE.api}/${id}/printRebtRfun`, {
            responseType: "arraybuffer",
        });
    },
    // Print Drawback !== 리베이트 인쇄 ==!
    printDrwb: (rfunNo: any) => {
        return api.get(`${BASE.api}/${rfunNo}/printDrawback`, {
            responseType: "arraybuffer",
        });
    },
    // Print Cash Deposit !== 현금 담보 인쇄 ==!
    printDpstSecu: (id: any) => {
        return api.get(`${BASE.api}/${id}/printDpstSecu`, {
            responseType: "arraybuffer",
        });
    },
    // Print Security !== 현금 담보 인쇄 ==!
    printSecuSecu: (id: any) => {
        return api.get(`${BASE.api}/${id}/printSecuSecu`, {
            responseType: "arraybuffer",
        });
    },
    // Print Overpayment Refund !== 과오납 환급 인쇄 ==!
    printOvpyRfun: (id: any) => {
        return api.get(`${BASE.api}/${id}/printOvpyRfun`, {
            responseType: "arraybuffer",
        });
    },
    // Save Approval Processing Information !== 결재 처리 정보 저장 ==!
    saveAprvPrcsInfo: (data: any) => {
        return api.put(`${BASE.comnAprvApi}/prcss`, {
            ...data,
        });
    },
    // Get Approval History Information !== 결재 이력 정보 조회 ==!
    getAprvHistInfo: (data: any) => {
        return api.get(`${BASE.comnAprvApi}/histInfo/${data.aprvId}`, {
            params: comnUtils.toGetParams({ usrDmnId: data.usrDmnId, aprvId: data.aprvId }),
        });
    },
    // Get Collection Approval History Information !== 징수 결재 이력 정보 조회 ==!
    getColtAprvHistInfo: (reffNo: any, coltBsopCd: any, page: number, size: number) => {
        return api.get(`${BASE.api}/aprvHist?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams({ reffNo: reffNo, coltBsopCd: coltBsopCd }),
        });
    },
    testZmpSend: (data: any) => {
        return api.post(`${BASE.zmp_api}/sendZanMalipoTest`, {
            ...data,
        });
    },
    reissBll: (data: any) => {
        return api.post(`${BASE.api_v1}/reissBll`, {
            ...data,
        });
    },
};

// Schema of TANSAD Infomation Form !== TANSAD 정보 폼 스키마 ==!
export const SF_COMN_TANSAD_INFO: TFormSchema = {
    id: "form_comn_tansadInfo",
    schema: {
        // TANSAD General
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        tansadDt: {
            type: "datetime",
            label: "L_TANSAD_DT",
        },
        regm: {
            type: "code",
            label: "L_REGM",
            area: "comnCd",
            comnCd: "OSC0072",
        },
        csclPlnCd: {
            type: "code",
            label: "L_CSCL_PLN",
            area: "comnCd",
            comnCd: "OSC0023",
        },
        exppnImppnTin: {
            type: "text",
        },
        exppnImppnNm: {
            type: "text",
        },
        exppnImppnTinNm: {
            type: "text",
            label: "L_EXPPN_IMPPN",
        },
        dcltTin: {
            type: "text",
        },
        dcltNm: {
            type: "text",
        },
        dcltTinNm: {
            type: "text",
            label: "L_DCLT_TIN",
        },
        dclrPrcssSttsCd: {
            type: "code",
            label: "L_PRCSS_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0018",
        },
    },
};

// Schema of TANSAD Search Form !== TANSAD 검색 폼 스키마 ==!
export const SF_COMN_TANSAD_SRCH: TFormSchema = {
    id: "form_comn_tansadSrch",
    schema: {
        tansadNo: {
            label: "L_TANSAD_NO",
        },
        cstmOfceCd: {
            type: "text",
            minLength: 4,
            maxLength: 4,
            required: true,
            size: 2,
            mask: [/[A-Z]/, /[A-Z]/, /[A-Z0-9]/, /[A-Z0-9]/],
            letterCase: "upper",
        },
        tansadYy: {
            type: "text",
            minLength: 2,
            maxLength: 2,
            required: true,
            size: 1,
            mask: [/\d/, /\d/],
        },
        tansadSrno: {
            type: "text",
            minLength: 7,
            maxLength: 7,
            required: true,
            size: 2,
            mask: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
        },
    },
};

// Schema of Bill Infomation Form !== 고지 정보 폼 스키마 ==!
export const SF_COMN_BLL_INFO: TFormSchema = {
    id: "form_comn_bllInfo",
    schema: {
        // TANSAD General
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        tansadDt: {
            type: "datetime",
            label: "L_TANSAD_DT",
        },
        regm: {
            type: "code",
            label: "L_REGM",
            area: "comnCd",
            comnCd: "OSC0072",
        },
        csclPlnCd: {
            type: "code",
            label: "L_CSCL_PLN",
            area: "comnCd",
            comnCd: "OSC0023",
        },
        exppnImppnTinNm: {
            type: "text",
            label: "L_EXPPN_IMPPN",
        },
        dcltTinNm: {
            type: "text",
            label: "L_DCLT_TIN",
        },
        dclrPrcssSttsCd: {
            type: "code",
            label: "L_PRCSS_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0018",
        },
        // Bill General
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllMgmtId: {
            type: "text",
            label: "L_BLL_MGMT_ID",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllSysmCd: {
            type: "text",
        },
        bllCstmOfceCd: {
            type: "text",
        },
        bllYy: {
            type: "text",
        },
        bllTpCd: {
            type: "text",
        },
        bllSrno: {
            type: "text",
        },
        bllDgr: {
            type: "text",
        },
        bllDt: {
            type: "datetime",
            label: "L_BLL_DT",
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        zmpDt: {
            type: "datetime",
            label: "L_ZMP_DT",
        },
        bllTpCdSec: {
            type: "code",
            label: "L_BLL_TP",
            area: "comnCd",
            comnCd: "OSC0166",
        },
        bllStatCd: {
            type: "code",
            label: "L_BLL_STAT",
            area: "comnCd",
            comnCd: "OSC0056",
        },
        pyerMgmtNoNm: {
            type: "text",
            label: "L_TXPR_TIN",
        },
        reffKey: {
            type: "text",
            label: "L_REFF_KEY",
        },
        reffKeyTpCd: {
            type: "code",
            label: "L_REFF_TP",
            area: "comnCd",
            comnCd: "OSC0067",
        },
        bllRgsrIdNm: {
            type: "text",
            label: "L_RGSR",
        },
        feeAmt: {
            type: "number",
            label: "L_FEE_AMT",
            thousandSeparator: true,
        },
        feeTzsAmt: {
            type: "number",
            label: "L_FEE_AMT_TZS",
            thousandSeparator: true,
        },
        payDdlnDt: {
            type: "date",
            label: "L_DDLN_DT",
        },
        lastDdlnXtnsDt: {
            type: "date",
            label: "L_DDLN_XTNS_DT",
        },
        xtnsDt: {
            type: "number",
            label: "L_XTNS_DD",
        },
        lastDdlnXtnsRsn: {
            type: "text",
            label: "L_DDLN_XTNS_RSN",
        },
        // Bill Cancel
        dspoDt: {
            type: "date",
            label: "L_CNCL_DT",
        },
        dspoCd: {
            type: "code",
            label: "L_CNCL_CD",
            area: "comnCd",
            comnCd: "OSC0057",
        },
        dspoRmrk: {
            type: "text",
            label: "L_CNCL_RSN",
            controlSize: 10,
        },
        // Bill Receipt
        payMetdCd: {
            type: "code",
            label: "L_PAY_METD",
            area: "comnCd",
            comnCd: "OSC0065",
        },
        trsyVchrNo: {
            type: "text",
            label: "L_TRSY_VCHR_NO",
        },
        rcptBnkCd: {
            type: "code",
            label: "L_RCPT_BNK",
            area: "bnkCd",
        },
        rcptBnkAcctNo: {
            type: "text",
            label: "L_RCPT_BNK_ACCT_NO",
        },
        rcptNo: {
            type: "text",
            label: "L_RCPT_NO",
        },
        manlRcptRsn: {
            type: "text",
            label: "L_MANL_RCPT_RSN",
        },
        rcptAmt: {
            type: "number",
            label: "L_RCPT_AMT",
            thousandSeparator: true,
        },
        rcptDt: {
            type: "date",
            label: "L_RCPT_DT",
        },
        currCd: {
            type: "text",
            label: "L_CURR_CD",
        },
        errRcveDt: {
            type: "datetime",
            label: "L_ERR_RCVE_DT",
        },
        errMsgCn: {
            type: "text",
            label: "L_ERR_CN",
        },
        orgCd: {
            type: "code",
            label: "L_OGA",
            area: "orgCd",
            viewType: "label",
        },
        appNm: {
            type: "text",
            label: "L_APP",
        },
    },
};

// Schema of Bill Search Form !== 고지 검색 폼 스키마 ==!
export const SF_COMN_BLL_SRCH: TFormSchema = {
    id: "form_comn_bllSrch",
    schema: {
        bllMgmtId: {
            label: "L_BLL_MGMT_ID",
        },
        bllNo: {
            label: "L_BLL_NO",
        },
        bllTpCd: {
            type: "text",
            minLength: 1,
            maxLength: 1,
            size: 1,
            mask: [/[A-Z]/],
            letterCase: "upper",
        },
        bllSysmCd: {
            type: "text",
            minLength: 4,
            maxLength: 4,
            size: 2,
            mask: [/[A-Z0-9]/, /[A-Z0-9]/, /[A-Z0-9]/, /[A-Z0-9]/],
            letterCase: "upper",
        },
        bllYy: {
            type: "text",
            minLength: 2,
            maxLength: 2,
            size: 1,
            mask: [/\d/, /\d/],
        },
        bllSrno: {
            type: "text",
            minLength: 7,
            maxLength: 7,
            size: 3,
            mask: [/[A-Z]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
        },
        bllDgr: {
            type: "text",
            minLength: 1,
            maxLength: 2,
            size: 1,
            mask: [/\d/, /\d/],
        },
    },
};

// Schema of Audit History List Grid !== 심사 이력 목록 그리드 스키마 ==!
export const SG_AUDT_HIST_LIST: TGridSchema = {
    id: "grid_audtHistList",
    options: {
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
    },
    head: [
        { cells: [{ header: "L_SRNO", binding: "audtSrno", width: 120 }] },
        { cells: [{ header: "L_AUDT_DT", binding: "audtDt", width: 150 }] },
        { cells: [{ header: "L_AUTR", binding: "autrId", width: 150 }] },
        { cells: [{ header: "L_AUDT_RMRK", binding: "audtRmrk", width: "*" }] },
        { cells: [{ header: "L_STTS", binding: "coltBsopStatCd", width: 250 }] },
    ],
    body: [
        { cells: [{ binding: "audtSrno" }] },
        { cells: [{ binding: "audtDt", type: "datetime" }] },
        { cells: [{ binding: "autrId" }] },
        { cells: [{ binding: "audtRmrk" }] },
        { cells: [{ binding: "coltBsopStatCd" }] },
    ],
};

// Schema of TANSAD Declaration General Tax Form !== TANSAD 신고서 일반 세금 폼 스키마 ==!
export const SF_TANSAD_DCLR_GNRL_TAX: TFormSchema = {
    id: "form_TansadDclrGnrlTax",
    schema: {
        totDtyTaxAmtLb: {
            label: "L_TOT_DTY_TAX_AMT",
        },
        totFeeAmtLb: {
            label: "L_TOT_FEE_AMT",
        },
        grteAmtLb: {
            label: "L_GRTE_AMT",
        },
        totPyblTaxAmtLb: {
            label: "L_TOT_PYBL_TAX_AMT",
        },
        totCstmValLb: {
            label: "L_TOT_CSTM_VAL",
        },
        totDtyTaxAmt: {
            type: "number",
            label: "L_TOT_DTY_TAX_AMT",
            only: "control",
        },
        totFeeAmt: {
            type: "number",
            label: "L_TOT_FEE_AMT",
            only: "control",
        },
        grteAmt: {
            type: "number",
            label: "L_GRTE_AMT",
            only: "control",
        },
        totPyblTaxAmt: {
            type: "number",
            label: "L_TOT_PYBL_TAX_AMT",
            only: "control",
        },
        totCstmVal: {
            type: "number",
            label: "L_TOT_CSTM_VAL",
            only: "control",
        },
    },
};

// Schema of TANSAD Declaration Type Tax List Form !== TANSAD 신고서 유형 세금 목록 폼 스키마 ==!
export const SG_TANSAD_DCLR_TP_TAX_LST: TGridSchema = {
    id: "grid_TansadDclrTpTaxLst",
    options: { height: "auto" },
    head: [
        { cells: [{ header: "L_TAX_TP", width: "32%" }] },
        { cells: [{ header: "L_TAX_BASE", width: "*" }] },
        { cells: [{ header: "L_TRIFF", width: "*" }] },
        { cells: [{ header: "L_AMT", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "taxTpCd", type: "code", area: "comnCd", comnCd: "OSC0032" }] },
        { cells: [{ binding: "taxBaseAmt", type: "number" }] },
        { cells: [{ binding: "taxRate" }] },
        { cells: [{ binding: "taxAmt", type: "number" }] },
    ],
};

// Schema of TANSAD Declaration Item Tax List Grid !== TANSAD 신고서 품목 세금 목록 그리드 스키마 ==!
export const SG_TANSAD_DCLR_ITM_TAX_LST: TGridSchema = {
    id: "grid_TansadDclrItmTaxLst",
    options: {
        height: "auto",
        pagination: "in",
    },
    head: [
        { cells: [{ header: "L_ITM_NO", width: "5%" }] },
        { cells: [{ header: "L_TAX_TP", width: "25%" }] },
        { cells: [{ header: "L_TAX_BASE", width: "12%" }] },
        { cells: [{ header: "L_TRIFF", width: "10%" }] },
        { cells: [{ header: "L_TAX_AMT", width: "13%" }] },
        { cells: [{ header: "L_RLF_TAX_AMT", width: "13%" }] },
        { cells: [{ header: "L_RLF_TP_CD", width: "10%" }] },
        { cells: [{ header: "L_PAY_MODE_CD", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "itmNo", type: "number" }] },
        { cells: [{ binding: "taxTpCd", type: "code", area: "comnCd", comnCd: "OSC0032" }] },
        { cells: [{ binding: "taxBaseAmt", type: "number" }] },
        { cells: [{ binding: "taxTrif", type: "number" }] },
        { cells: [{ binding: "taxAmt", type: "number" }] },
        { cells: [{ binding: "rlfTaxAmt", type: "number" }] },
        { cells: [{ binding: "rlfTpCd" }] },
        { cells: [{ binding: "payModeCd" }] },
    ],
};
